/**
 * /assessment/v2/dossier — the full written WeKIT™ Career Clarity 360 Passport.
 *
 * Same state, same engine, same numbers as the result screen. This route
 * exists because the result screen answers "what fits me?" in ninety
 * seconds, and a parent sitting down with a cup of tea wants the long
 * version with the working shown.
 */
import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Download, Loader2, Image as ImageIcon } from "lucide-react";
import { AssessmentV2Provider, useAssessmentV2 } from "@/features/assessment-v2/store";
import { useEffectiveRunState } from "@/features/assessment-v2/useEffectiveRunState";
import { parseBand } from "@/features/assessment-v2/band";
import { useDossier } from "@/features/assessment-v2/useDossier";
import { useCanonicalTakeover } from "@/features/assessment-v2/useCanonicalReport";
import { DossierBody } from "@/features/assessment-v2/components/DossierSections";
import ShareDossierLink from "@/features/assessment-v2/components/ShareDossierLink";
import { downloadDossierPdf, dossierFileName } from "@/features/assessment-v2/downloadDossierPdf";
import { toast } from "@/hooks/use-toast";
import "@/features/assessment-v2/dossier-print.css";

function DossierInner() {
  const { state: localState, band } = useAssessmentV2();
  // Same rule as the result screen: never write a report from an empty shell.
  const { state } = useEffectiveRunState(localState, band);
  const takeover = useCanonicalTakeover(band);
  const { dossier, error } = useDossier(state, band, {
    reportHash: takeover.canonical?.reportHash ?? null,
    attemptId: takeover.sessionId,
  });
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (!dossier) return;
    setDownloading(true);
    try {
      await downloadDossierPdf(dossierFileName(dossier.learnerName));
    } catch (e) {
      toast({
        title: "Couldn't build the PDF",
        description: e instanceof Error ? e.message : "Use Print instead — it saves as PDF too.",
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  };


  if (error) {
    return (
      <div className="min-h-dvh bg-background text-foreground grid place-items-center px-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="font-display text-2xl">Your report didn't load. Nothing you said is lost.</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Link
            to="/assessment/v2/result"
            className="inline-flex min-h-[48px] items-center px-6 rounded-2xl bg-primary text-primary-foreground"
          >
            Back to your results
          </Link>
        </div>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="min-h-dvh bg-background text-foreground grid place-items-center px-6">
        <p className="text-sm text-muted-foreground animate-pulse">
          Writing your report…
        </p>
      </div>
    );
  }

  return (
    <div className="dossier-root min-h-dvh bg-background text-foreground">
      <div className="no-print sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto max-w-3xl px-6 h-16 flex items-center justify-between gap-4">
          <Link
            to="/assessment/v2/result"
            className="inline-flex items-center gap-2 min-h-[44px] text-sm text-muted-foreground
              hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Your results
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to={`/clarity-card?band=${dossier.band}`}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl border border-border px-4
                text-sm hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <ImageIcon className="h-4 w-4" aria-hidden="true" /> Clarity Card
            </Link>
            <ShareDossierLink dossier={dossier} />

            <button
              type="button"
              onClick={download}
              disabled={downloading}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-2xl border border-border
                text-sm hover:bg-muted disabled:opacity-60
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {downloading
                ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <Download className="h-4 w-4" aria-hidden="true" />}
              Download PDF
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-2xl bg-primary
                text-primary-foreground text-sm font-medium hover:opacity-90
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Printer className="h-4 w-4" aria-hidden="true" /> Print
            </button>
          </div>
        </div>
      </div>

      <main>
        <DossierBody d={dossier} />
      </main>

    </div>
  );
}

export default function AssessmentV2Dossier() {
  const [params] = useSearchParams();
  const band = parseBand(params.get("band"));
  return (
    <AssessmentV2Provider key={band} band={band}>
      <DossierInner />
    </AssessmentV2Provider>
  );
}
